import {
  useEffect,
  type Dispatch,
  type SetStateAction
} from 'react';
import FiltersComponent from '../../common/components/filters/FiltersComponent';
import NoCrawlData from '../components/common/NoCrawlData';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import PageDrawer from '../components/pages/PageDrawer';
import ExternalLinksChart from '../components/external-link/ExternalLinksChart';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import { ExternalLinkColumns } from '../config/columnsConfig';
import { Card, Col, Placeholder, Row } from 'react-bootstrap';
import {
  useGetCrawlExternalLinksMutation,
  useCountCrawlExternalLinksQuery,
  useGetChartDataQuery
} from '@/app/Crawl/api/externalLinksApi';
import { useOutletContext, useParams } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useExportTableMutation } from '@/app/Crawl/api/exportApi';
import useDomain from '@/hooks/useDomain';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';

// صفحه پیوند خارجی
type BreadcrumbItem = {
  text: string;
  href: string;
};

type CrawlOutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type LinksData = {
  job_page?: string;
  total?: number;
  rows?: unknown[];
};

type LinksResponse = {
  data?: LinksData;
};

const ExternalLink = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const access = useToolAccess('crawl');

  const [, setBreadcrumbData] =
    useOutletContext<CrawlOutletContext>();
  useEffect(() => {
    setBreadcrumbData([{ text: 'پیوند خارجی', href: '#' }]);
  }, []);

  const { domain_slug } = useDomain();
  const { slug } = useParams();
  useGetUserInfoQuery();
  const job_page = slug === 'last' ? domain_slug : slug;
  const [getAnchorText] = useGetCrawlExternalLinksMutation();
  const [exportTable] = useExportTableMutation();
  const {
    data: rawData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useCountCrawlExternalLinksQuery(
    { job_page, domain_slug },
    { skip: access.isLoading || !access.haveAccess || !job_page || !domain_slug }
  );
  const data = rawData as LinksResponse | undefined;
  const linksData = data?.data;

  const { data: chartData, isLoading: isChartLoading } = useGetChartDataQuery(
    {
      job_page,
      domain_slug,
      group_by: 'domain_link',
      link_type: 'external',
      limit: 20
    },
    { skip: access.isLoading || !access.haveAccess || !job_page || !domain_slug }
  );

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار خزش" />;
  }

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }
  if (!isLoading && !linksData) return <NoCrawlData />;

  return (
    <Row>
      <Col xxl={12}>
        {!isChartLoading && chartData && !isLoading ? (
          <ExternalLinksChart data={chartData} />
        ) : (
          <ChartSkeleton />
        )}
      </Col>
      <Col xxl={12}>
        <FiltersComponent columns={ExternalLinkColumns(isMdUp)} />
      </Col>
      <Col xxl={12}>
        {!isLoading ? (
          <AgGridWrapper
            table_name={location.pathname.split('/').slice(1, 3).join('/')}
            fetchFunction={getAnchorText}
            height="746px"
            columns={ExternalLinkColumns(isMdUp)}
            additionalData={{
              job_page: linksData?.job_page,
              domain_slug
            }}
            settings={{
              pagination: true,
              export: {
                enabled: true,
                fetchFunction: exportTable
              }
            }}
            total={linksData?.total}
            data={linksData?.rows}
          />
        ) : (
          <AgGridSkeleton height="696px" />
        )}
        <PageDrawer />
      </Col>
    </Row>
  );
};

export default ExternalLink;
// لودینگ اسکلت پیوند خارجی
const ChartSkeleton = () => (
  <Card className="mb-3">
    <FalconCardHeader
      title={
        <div>
          پیوند خارجی خزش <HelpTooltip html="درحال بارگذاری" />
        </div>
      }
      titleTag="h6"
      className="py-2"
      light
    />
    <Card.Body className="px-6">
      <div style={{ height: '23rem' }}>
        <div
          className="mt-4 px-8 d-flex gap-1 flex-column"
          style={{ direction: 'ltr' }}
        >
          <Placeholder size="lg" style={{ width: '100%' }} />
          <Placeholder size="lg" style={{ width: '70%' }} />
          <Placeholder size="lg" style={{ width: '60%' }} />
          <Placeholder size="lg" style={{ width: '70%' }} />
          <Placeholder size="lg" style={{ width: '50%' }} />
          <Placeholder size="lg" style={{ width: '35%' }} />
          <Placeholder size="lg" style={{ width: '29%' }} />
          <Placeholder size="lg" style={{ width: '20%' }} />
          <Placeholder size="lg" style={{ width: '10%' }} />
          <Placeholder size="lg" style={{ width: '3%' }} />
        </div>
      </div>
    </Card.Body>
  </Card>
);
